import * as React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { Button, Stack, Stepper, Step, StepLabel, StepConnector, stepConnectorClasses, TextField, useMediaQuery, Box, FormControl, MenuItem, useTheme } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import Header from '../../../components/Header';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Country, State, City } from 'country-state-city';
import { addVendor, editVendor, viewVendor } from '../../../redux/slice/vendors.slice';

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 22,
    },
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            backgroundImage: 'linear-gradient( 95deg,#222222 0%, #FFFFFF 120%)',
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            backgroundImage: 'linear-gradient( 95deg,#222222 0%, #FFFFFF 120%)',
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        height: 3,
        border: 0,
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
        borderRadius: 1,
    },
}));

const ColorlibStepIconRoot = styled('div')(({ theme, ownerState }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
    zIndex: 1,
    color: '#fff',
    width: 50,
    height: 50,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    ...(ownerState.active && {
        border: theme.palette.mode === 'dark' ? '2px solid white' : '2px solid black',
        boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
    }),
    ...(ownerState.completed && {
        border: theme.palette.mode === 'dark' ? '2px solid white' : '2px solid black',
    }),
    ...(ownerState.error && {
        border: '2px solid red',
    }),
}));

function ColorlibStepIcon(props) {
    const { active, completed, className, icon, onClick, theme, error } = props;
    const icons = {
        1: <img src="/assets/images/icons/file.svg" style={{ filter: theme.palette.mode === 'light' ? 'invert(1) brightness(2)' : 'invert(0) brightness(1)' }} width="25" height="26" alt='' />,
        2: <img src="/assets/images/icons/Customer.svg" style={{ filter: theme.palette.mode === 'light' ? 'invert(1) brightness(2)' : 'invert(0) brightness(1)' }} width="25" height="26" alt='' />,
        3: <img src="/assets/images/icons/file.svg" style={{ filter: theme.palette.mode === 'light' ? 'invert(1) brightness(2)' : 'invert(0) brightness(1)' }} width="25" height="26" alt='' />,
        4: <img src="/assets/images/icons/Customer.svg" style={{ filter: theme.palette.mode === 'light' ? 'invert(1) brightness(2)' : 'invert(0) brightness(1)' }} width="25" height="26" alt='' />,
        5: <img src="/assets/images/icons/file.svg" style={{ filter: theme.palette.mode === 'light' ? 'invert(1) brightness(2)' : 'invert(0) brightness(1)' }} width="25" height="26" alt='' />,
    };

    return (
        <ColorlibStepIconRoot ownerState={{ completed, active, error }} className={className} onClick={onClick}>
            {icons[String(icon)]}
        </ColorlibStepIconRoot>
    );
}

ColorlibStepIcon.propTypes = {
    active: PropTypes.bool,
    className: PropTypes.string,
    completed: PropTypes.bool,
    icon: PropTypes.node,
    onClick: PropTypes.func,
    error: PropTypes.bool,
};
const steps = ['Vendor Details', 'Contact Person Details', 'Business Document Details', 'Vendor Type', 'Bank Details'];

const validationSchemas =
    Yup.object({
        businessName: Yup.string().required('Business Name is required'),
        officeAddress: Yup.string().required('Office Address is required'),
        pincode: Yup.string().matches(/^\d{6}$/, 'Pincode must be 6 digits').required('Pincode is required'),
        city: Yup.string().required('City is required'),
        district: Yup.string().required('district is required'),
        state: Yup.string().required('State is required'),
        country: Yup.string().required('Country is required'),
        companyEmailId: Yup.string().email("Enter a valid email").required("Please enter user email"),
        contactPersonalDetails: Yup.array().of(
            Yup.object({
                contactPersonName: Yup.string().required('Contact Person Name is required'),
                designation: Yup.string().required('Designation is required'),
                department: Yup.string().required('Department is required'),
                contactNumber: Yup.string().matches(/^\d{10}$/, 'Phone number must be 10 digits').required('Phone Number is required'),
                emailId: Yup.string().email("Enter a valid email").required("Please enter user email"),
            })
        ),
        registrationType: Yup.string().required('Registration Type is required'),
        vendorType: Yup.string().required('Vendor Type is required'),
        bankDetails: Yup.array().of(
            Yup.object({
                bankName: Yup.string().required('Bank Name is required'),
                branchName: Yup.string().required('Branch Name is required'),
                accountNumber: Yup.string().required('Account Number is required'),
                IFSCCode: Yup.string().required('IFSC Code is required'),
                accountType: Yup.string()
                    .required("Account Type is required")
                    .oneOf(["Current", "Saving", "CashCredit"], "Invalid Account Type")
            })
        )
    });

const initialValues = {
    businessName: '',
    officeAddress: '',
    pincode: '',
    district: '',
    city: '',
    state: '',
    country: 'IN',
    companyEmailId: '',
    gstNumber: '',
    contactPersonalDetails: [{
        contactPersonName: '',
        designation: '',
        department: '',
        emailId: '',
        contactNumber: '',
    }],
    registrationType: '',
    vendorType: '',
    bankDetails: [{
        bankName: '',
        branchName: '',
        accountNumber: '',
        accountType: '',
        IFSCCode: ''
    }]
};

const errorBelongsToStep = (errorKey, stepIndex) => {
    if (stepIndex === 0) {
        return ['businessName', 'officeAddress', 'pincode', 'city', 'district', 'state', 'country', 'companyEmailId'].includes(errorKey);
    }
    else if (stepIndex === 1) {
        return errorKey.startsWith('contactPersonalDetails');
    }
    else if (stepIndex === 2) {
        return ['registrationType', 'gstNumber', 'panNumber', 'UdyamRegistration'].includes(errorKey);
    }
    else if (stepIndex === 3) {
        return ['vendorType'].includes(errorKey);
    }
    else if (stepIndex === 4) {
        return errorKey.startsWith('bankDetails');
    }
    return false;
};

export default function AddVendor() {
    const [activeStep, setActiveStep] = React.useState(0);
    const [stepsWithErrors, setStepsWithErrors] = React.useState([false, false, false, false, false]);
    const [touchedSteps, setTouchedSteps] = React.useState([false, false, false]);
    const [contactPerson, setContactPerson] = useState(initialValues.contactPersonalDetails);
    const [bankData, setBankData] = useState(initialValues.bankDetails);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state } = useLocation();
    const isMobile = useMediaQuery('(max-width:500px)');

    const id = state?._id;
    const isLastStep = activeStep === steps.length - 1;

    const handleNext = (values) => {
        if (!isLastStep) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        } else {
            setTouchedSteps(steps.map(() => true));
            validateForm().then((formErrors) => {
                const newStepsWithErrors = [false, false, false, false, false];
                Object.keys(formErrors).forEach((errorKey) => {
                    steps.forEach((_, stepIndex) => {
                        if (errorBelongsToStep(errorKey, stepIndex)) {
                            newStepsWithErrors[stepIndex] = true;
                        }
                    });
                });

                setStepsWithErrors(newStepsWithErrors);

                if (Object.keys(formErrors).length === 0) {
                    const submissionValues = {
                        ...values,
                        contactPersonalDetails: values.contactPersonalDetails,
                        bankDetails: values.bankDetails
                    };

                    if (id) {
                        dispatch(editVendor({ id: values._id, ...submissionValues, navigate }));
                    } else {
                        dispatch(addVendor({ data: submissionValues, navigate }));
                    }

                    setActiveStep(0);
                    setStepsWithErrors([false, false, false, false, false]);
                } else {
                    console.log("Form validation errors:", formErrors);
                    const newStepsWithErrors = steps.map((_, index) => {
                        return index <= activeStep && Object.keys(formErrors).length > 0;
                    });
                    setStepsWithErrors(newStepsWithErrors);
                    setFieldTouched('businessName', true);
                    setFieldTouched('officeAddress', true);
                    setFieldTouched('pincode', true);
                    setFieldTouched('city', true);
                    setFieldTouched('district', true);
                    setFieldTouched('state', true);
                    setFieldTouched('country', true);
                    setFieldTouched('companyEmailId', true);

                    values.contactPersonalDetails.forEach((_, index) => {
                        setFieldTouched(`contactPersonalDetails.${index}.contactPersonName`, true);
                        setFieldTouched(`contactPersonalDetails.${index}.designation`, true);
                        setFieldTouched(`contactPersonalDetails.${index}.department`, true);
                        setFieldTouched(`contactPersonalDetails.${index}.contactNumber`, true);
                        setFieldTouched(`contactPersonalDetails.${index}.emailId`, true);
                    });

                    values.bankDetails.forEach((_, index) => {
                        setFieldTouched(`bankDetails.${index}.bankName`, true);
                        setFieldTouched(`bankDetails.${index}.branchName`, true);
                        setFieldTouched(`bankDetails.${index}.accountNumber`, true);
                        setFieldTouched(`bankDetails.${index}.IFSCCode`, true);
                        setFieldTouched(`bankDetails.${index}.accountType`, true);
                    });
                }
            });
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };
    const handleStep = (step) => {
        setActiveStep(step);
    };
    React.useEffect(() => {
        if (id) {
            dispatch(viewVendor(id)).then((response) => {
                if (response.payload) {
                    const vendorData = response.payload;
                    setValues({
                        ...vendorData,
                        contactPersonalDetails: [...vendorData.contactPersonalDetails],
                        bankDetails: [...vendorData.bankDetails]
                    });
                    setContactPerson([...vendorData.contactPersonalDetails]);
                    setBankData([...vendorData.bankDetails]);
                    setStates(State.getStatesOfCountry(vendorData.country));
                    setDistricts(City.getCitiesOfState(vendorData.country, vendorData.state));
                }
            }).catch(error => {
                console.error("Error fetching data:", error);
            });
        }
    }, [id, dispatch]);

    const handleAddRow = () => {
        setContactPerson([...contactPerson, {
            contactPersonName: '',
            designation: '',
            department: '',
            emailId: '',
            contactNumber: ''
        }]);
    };
    const handleAddBank = () => {
        setBankData([...bankData, {
            bankName: '',
            accountNumber: '',
            accountType: '',
            branchName: '',
            IFSCCode: ''
        }])
    }
    React.useEffect(() => {
        const defaultCountry = 'IN';
        setCountries(Country.getAllCountries());
        setStates(State.getStatesOfCountry(defaultCountry));
        setDistricts([]); 
    }, []);

    const handleStateChange = (e) => {
        const stateId = e.target.value;
        handleChange(e);
        setDistricts(City.getCitiesOfState(values.country, stateId));
    };

    const isNonMobile = useMediaQuery("(min-width:600px)");
    const theme = useTheme();

    const { handleSubmit, handleBlur, handleChange, values, touched, errors, setFieldValue, setValues, validateForm, setFieldError, setFieldTouched } = useFormik({
        initialValues: { ...initialValues, contactPersonalDetails: contactPerson, bankDetails: bankData },
        validationSchema: validationSchemas,
        onSubmit: handleNext,
    });

    React.useEffect(() => {
        if (touchedSteps.some(touched => touched)) {  
            validateForm().then((formErrors) => {
                const newStepsWithErrors = [false, false, false, false, false];

                Object.keys(formErrors).forEach((errorKey) => {
                    steps.forEach((_, stepIndex) => {
                        if (errorBelongsToStep(errorKey, stepIndex)) {
                            newStepsWithErrors[stepIndex] = true;
                        }
                    });
                });

                setStepsWithErrors(newStepsWithErrors);
            });
        }
    }, [values, touchedSteps]); 

    const isSmallScreen = useMediaQuery("(max-width:425px)"); 

    const handleCountryChange = (e) => {
        const countryId = e.target.value;
        handleChange(e); 
        setStates(State.getStatesOfCountry(countryId)); 
        setDistricts([]); 
    };

    return (
        <Box m="2px">
            {id ? (
                <Header
                    title="UPDATE VENDOR"
                    subtitle="Update Your Vendor Detail"
                />
            ) : (
                <Header
                    title="ADD VENDOR"
                    subtitle="Manage Your Vendor Detail"
                />
            )}
            <form onSubmit={handleSubmit} >
                <Stack sx={{ width: '100%', marginTop: '30px' }} spacing={4}>
                    {isSmallScreen ? (
                        <>
                            <Stepper alternativeLabel activeStep={activeStep} connector={<ColorlibConnector />}>
                                {steps.slice(0, 3).map((label, index) => ( 
                                    <Step key={label}>
                                        <StepLabel StepIconComponent={(props) => (
                                            <ColorlibStepIcon
                                                {...props}
                                                theme={theme}
                                                icon={index + 1}
                                                onClick={() => handleStep(index)}
                                                error={touchedSteps[index] && stepsWithErrors[index]}
                                            />
                                        )}>
                                            {label}
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                            <Stepper alternativeLabel activeStep={activeStep} connector={<ColorlibConnector />}>
                                {steps.slice(3).map((label, index) => (
                                    <Step key={label}>
                                        <StepLabel StepIconComponent={(props) => (
                                            <ColorlibStepIcon
                                                {...props}
                                                theme={theme}
                                                icon={index + 4} 
                                                onClick={() => handleStep(index + 3)}
                                                error={touchedSteps[index + 3] && stepsWithErrors[index + 3]}
                                            />
                                        )}>
                                            {label}
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </>
                    ) : (
                        <Stepper alternativeLabel activeStep={activeStep} connector={<ColorlibConnector />}>
                            {steps.map((label, index) => (
                                <Step key={label}>
                                    <StepLabel StepIconComponent={(props) => (
                                        <ColorlibStepIcon
                                            {...props}
                                            theme={theme}
                                            icon={index + 1}
                                            onClick={() => handleStep(index)}
                                            error={touchedSteps[index] && stepsWithErrors[index]}
                                        />
                                    )}>
                                        {label}
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    )}
                    {activeStep === 0 && (
                        <>
                            <Box
                                display="grid"
                                gap="30px"
                                gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                                sx={{
                                    "& > div": { gridColumn: isNonMobile ? undefined : "span 3" },
                                }}
                            >
                                <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Business Name :"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.businessName}
                                        name="businessName"
                                        error={!!touched.businessName && !!errors.businessName}
                                        helperText={touched.businessName && errors.businessName}
                                        sx={{ gridColumn: "span 2" }}
                                        InputLabelProps={{
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                    />
                                </FormControl>
                                <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        variant="filled"
                                        type="text"
                                        label="Office Address : "
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.officeAddress}
                                        name="officeAddress"
                                        error={!!touched.officeAddress && !!errors.officeAddress}
                                        helperText={touched.officeAddress && errors.officeAddress}
                                        sx={{ gridColumn: "span 2" }}
                                        InputLabelProps={{
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                    />
                                </FormControl>
                                <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Pincode :"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.pincode}
                                        name="pincode"
                                        error={!!touched.pincode && !!errors.pincode}
                                        helperText={touched.pincode && errors.pincode}
                                        sx={{ gridColumn: "span 2" }}
                                        InputLabelProps={{
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Box>
                            <Box
                                display="grid"
                                gap="30px"
                                gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                                sx={{
                                    "& > div": { gridColumn: isNonMobile ? undefined : "span 3" },
                                }}
                            >
                               
                                <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                    <TextField
                                        select
                                        fullWidth
                                        variant="filled"
                                        label="State :"
                                        onBlur={handleBlur}
                                        onChange={handleStateChange}
                                        value={values.state}
                                        name="state"
                                        error={!!touched.state && !!errors.state}
                                        helperText={touched.state && errors.state}
                                        InputLabelProps={{
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                        SelectProps={{
                                            MenuProps: {
                                                anchorOrigin: {
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                },
                                                transformOrigin: {
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                },
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300,
                                                    },
                                                },
                                            },
                                        }}
                                    >
                                        {states.map((state) => (
                                            <MenuItem key={state.isoCode} value={state.isoCode}>
                                                {state.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </FormControl>
                                <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                    <TextField
                                        select
                                        fullWidth
                                        variant="filled"
                                        label="District  :"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.district}
                                        name="district"
                                        error={!!touched.district && !!errors.district}
                                        helperText={touched.district && errors.district}
                                        sx={{ gridColumn: "span 2" }}
                                        InputLabelProps={{
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                        SelectProps={{
                                            MenuProps: {
                                                anchorOrigin: {
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                },
                                                transformOrigin: {
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                },
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300,
                                                    },
                                                },
                                            },
                                        }}
                                    >
                                        {districts.map((district) => (
                                            <MenuItem key={district.name} value={district.name}>
                                                {district.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </FormControl>
                                <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="City :"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.city}
                                        name="city"
                                        error={!!touched.city && !!errors.city}
                                        helperText={touched.city && errors.city}
                                        sx={{ gridColumn: "span 2" }}
                                        InputLabelProps={{
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Box>
                            <Box
                                display="grid"
                                gap="30px"
                                gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                                sx={{
                                    "& > div": { gridColumn: isNonMobile ? undefined : "span 3" },
                                }}
                            >

                                <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="email"
                                        label="Company Email Id :"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.companyEmailId}
                                        name="companyEmailId"
                                        error={!!touched.companyEmailId && !!errors.companyEmailId}
                                        helperText={touched.companyEmailId && errors.companyEmailId}
                                        sx={{ gridColumn: "span 2" }}
                                        InputLabelProps={{
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                    />
                                </FormControl>

                            </Box>

                        </>
                    )}
                    {activeStep === 1 && (
                        <>
                            {contactPerson && contactPerson.length > 0 && contactPerson.map((person, index) => (
                                <Box
                                    key={index}
                                    display="grid"
                                    gap="30px"
                                    gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                                    sx={{
                                        "& > div": { gridColumn: isNonMobile ? undefined : "span 3" },
                                    }}
                                >
                                    <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            label="Contact Person Name :"
                                            onBlur={handleBlur}
                                            onChange={e => setFieldValue(`contactPersonalDetails[${index}].contactPersonName`, e.target.value)}
                                            value={values.contactPersonalDetails?.[index]?.contactPersonName || ''}
                                            name={`contactPersonalDetails.${index}.contactPersonName`}
                                            error={!!touched.contactPersonalDetails?.[index]?.contactPersonName && !!errors.contactPersonalDetails?.[index]?.contactPersonName}
                                            helperText={touched.contactPersonalDetails?.[index]?.contactPersonName && errors.contactPersonalDetails?.[index]?.contactPersonName}
                                            sx={{ gridColumn: "span 2" }}
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text.primary
                                                }
                                            }}
                                        />
                                    </FormControl>
                                    <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            label="Designation : "
                                            onBlur={handleBlur}
                                            onChange={e => setFieldValue(`contactPersonalDetails[${index}].designation`, e.target.value)}
                                            value={values.contactPersonalDetails?.[index]?.designation || ''}
                                            name={`contactPersonalDetails.${index}.designation`}
                                            error={!!touched.contactPersonalDetails?.[index]?.designation && !!errors.contactPersonalDetails?.[index]?.designation}
                                            helperText={touched.contactPersonalDetails?.[index]?.designation && errors.contactPersonalDetails?.[index]?.designation}
                                            sx={{ gridColumn: "span 2" }}
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text.primary
                                                }
                                            }}
                                        />
                                    </FormControl>
                                    <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            label="Department :"
                                            onBlur={handleBlur}
                                            onChange={e => setFieldValue(`contactPersonalDetails[${index}].department`, e.target.value)}
                                            value={values.contactPersonalDetails?.[index]?.department || ''}
                                            name={`contactPersonalDetails.${index}.department`}
                                            error={!!touched.contactPersonalDetails?.[index]?.department && !!errors.contactPersonalDetails?.[index]?.department}
                                            helperText={touched.contactPersonalDetails?.[index]?.department && errors.contactPersonalDetails?.[index]?.department}
                                            sx={{ gridColumn: "span 2" }}
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text.primary
                                                }
                                            }}
                                        />
                                    </FormControl>

                                    <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                        <TextField
                                            id="contactNumber"
                                            label="Contact Number :"
                                            variant="filled"
                                            onBlur={handleBlur}
                                            onChange={e => setFieldValue(`contactPersonalDetails[${index}].contactNumber`, e.target.value)}
                                            value={values.contactPersonalDetails?.[index]?.contactNumber || ''}
                                            name={`contactPersonalDetails.${index}.contactNumber`}
                                            error={!!touched.contactPersonalDetails?.[index]?.contactNumber && !!errors.contactPersonalDetails?.[index]?.contactNumber}
                                            helperText={touched.contactPersonalDetails?.[index]?.contactNumber && errors.contactPersonalDetails?.[index]?.contactNumber}
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text.primary
                                                }
                                            }}
                                        />
                                    </FormControl>
                                    <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                        <TextField
                                            id="emailId"
                                            label="Email Id :"
                                            variant="filled"
                                            onBlur={handleBlur}
                                            onChange={e => setFieldValue(`contactPersonalDetails[${index}].emailId`, e.target.value)}
                                            value={values.contactPersonalDetails?.[index]?.emailId || ''}
                                            name={`contactPersonalDetails.${index}.emailId`}
                                            error={!!touched.contactPersonalDetails?.[index]?.emailId && !!errors.contactPersonalDetails?.[index]?.emailId}
                                            helperText={touched.contactPersonalDetails?.[index]?.emailId && errors.contactPersonalDetails?.[index]?.emailId}
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text.primary
                                                }
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                            ))}
                            <Box display="flex" justifyContent="flex-end" m={1}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddRow}
                                >
                                    Add Contact Person
                                </Button>
                            </Box>
                        </>
                    )}

                    {activeStep === 2 && (
                        <>
                            <Box
                                display="grid"
                                gap="30px"
                                gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                                sx={{
                                    "& > div": { gridColumn: isNonMobile ? undefined : "span 3" },
                                }}
                            >
                                <TextField
                                    margin="dense"
                                    id="registrationType"
                                    name="registrationType"
                                    label="Registration Type :"
                                    select
                                    fullWidth
                                    variant="filled"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.registrationType}
                                    error={touched.registrationType && Boolean(errors.registrationType)}
                                    helperText={touched.registrationType && errors.registrationType}
                                    InputLabelProps={{
                                        style: {
                                            color: theme.palette.text.primary
                                        }
                                    }}
                                >
                                    <MenuItem value="Registered">Registered</MenuItem>
                                    <MenuItem value="UnRegistered">UnRegistered</MenuItem>
                                </TextField>
                                <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Gst Number :"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.gstNumber}
                                        name="gstNumber"
                                        error={!!touched.gstNumber && !!errors.gstNumber}
                                        helperText={touched.gstNumber && errors.gstNumber}
                                        sx={{ gridColumn: "span 2" }}
                                        InputLabelProps={{
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                        disabled={values.registrationType === "UnRegistered"}
                                    />
                                </FormControl>
                                <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Pan Number :"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.panNumber}
                                        name="panNumber"
                                        error={!!touched.panNumber && !!errors.panNumber}
                                        helperText={touched.panNumber && errors.panNumber}
                                        sx={{ gridColumn: "span 2" }}
                                        InputLabelProps={{
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Box>
                            <Box
                                display="grid"
                                gap="30px"
                                gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                                sx={{
                                    "& > div": { gridColumn: isNonMobile ? undefined : "span 3" },
                                }}
                            >
                                <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="MSME/Udyam Registration :"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.UdyamRegistration}
                                        name="UdyamRegistration"
                                        error={!!touched.UdyamRegistration && !!errors.UdyamRegistration}
                                        helperText={touched.UdyamRegistration && errors.UdyamRegistration}
                                        sx={{ gridColumn: "span 2" }}
                                        InputLabelProps={{
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Box>
                        </>
                    )}
                    {activeStep === 3 && (
                        <>
                            <Box
                                display="grid"
                                gap="30px"
                                gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                                sx={{
                                    "& > div": { gridColumn: isNonMobile ? undefined : "span 3" },
                                }}
                            >
                                <TextField
                                    margin="dense"
                                    id="vendorType"
                                    name="vendorType"
                                    label="Vendor Type :"
                                    select
                                    fullWidth
                                    variant="filled"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.vendorType}
                                    error={touched.vendorType && Boolean(errors.vendorType)}
                                    helperText={touched.vendorType && errors.vendorType}
                                    InputLabelProps={{
                                        style: {
                                            color: theme.palette.text.primary
                                        }
                                    }}
                                >
                                    <MenuItem value="Government">Government</MenuItem>
                                    <MenuItem value="Supplier">Supplier</MenuItem>
                                    <MenuItem value="Transportar">Transportar</MenuItem>
                                    <MenuItem value="Bank">Bank</MenuItem>
                                </TextField>
                            </Box>
                        </>
                    )}
                    {activeStep === 4 && (
                        <>
                            {bankData && bankData.length > 0 && bankData.map((bank, index) => (
                                <Box
                                    key={index}
                                    display="grid"
                                    gap="30px"
                                    gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                                    sx={{
                                        "& > div": { gridColumn: isNonMobile ? undefined : "span 3" },
                                    }}
                                >
                                    <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            label="Bank Name :"
                                            onBlur={handleBlur}
                                            onChange={e => setFieldValue(`bankDetails[${index}].bankName`, e.target.value)}
                                            value={values.bankDetails?.[index]?.bankName || ''}
                                            name={`bankDetails.${index}.bankName`}
                                            error={!!touched.bankDetails?.[index]?.bankName && !!errors.bankDetails?.[index]?.bankName}
                                            helperText={touched.bankDetails?.[index]?.bankName && errors.bankDetails?.[index]?.bankName}
                                            sx={{ gridColumn: "span 2" }}
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text.primary
                                                }
                                            }}
                                        />
                                    </FormControl>
                                    <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            label="Branch Name:"
                                            onBlur={handleBlur}
                                            name={`bankDetails.${index}.branchName`}
                                            onChange={e => setFieldValue(`bankDetails[${index}].branchName`, e.target.value)}
                                            value={values.bankDetails?.[index]?.branchName || ''}
                                            error={!!touched.bankDetails?.[index]?.branchName && !!errors.bankDetails?.[index]?.branchName}
                                            helperText={touched.bankDetails?.[index]?.branchName && errors.bankDetails?.[index]?.branchName}
                                            sx={{ gridColumn: "span 2" }}
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text.primary
                                                }
                                            }}
                                        />
                                    </FormControl>
                                    <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            label="Account Number:"
                                            onBlur={handleBlur}
                                            onChange={e => setFieldValue(`bankDetails[${index}].accountNumber`, e.target.value)}
                                            value={values.bankDetails?.[index]?.accountNumber || ''}
                                            name={`bankDetails.${index}.accountNumber`}
                                            error={!!touched.bankDetails?.[index]?.accountNumber && !!errors.bankDetails?.[index]?.accountNumber}
                                            helperText={touched.bankDetails?.[index]?.accountNumber && errors.bankDetails?.[index]?.accountNumber}
                                            sx={{ gridColumn: "span 2" }}
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text.primary
                                                }
                                            }}
                                        />
                                    </FormControl>
                                    <TextField
                                        margin="dense"
                                        id="accountType"
                                        label="Account Type :"
                                        select
                                        fullWidth
                                        variant="filled"
                                        onBlur={handleBlur}
                                        name={`bankDetails.${index}.accountType`}
                                        onChange={e => setFieldValue(`bankDetails[${index}].accountType`, e.target.value)}
                                        value={values.bankDetails?.[index]?.accountType || ''}
                                        error={!!touched.bankDetails?.[index]?.accountType && !!errors.bankDetails?.[index]?.accountType}
                                        helperText={touched.bankDetails?.[index]?.accountType && errors.bankDetails?.[index]?.accountType}
                                        InputLabelProps={{
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                    >
                                        <MenuItem value="Current">Current</MenuItem>
                                        <MenuItem value="Saving">Saving</MenuItem>
                                        <MenuItem value="CashCredit">CashCredit</MenuItem>
                                    </TextField>
                                    <FormControl variant="filled" sx={{ m: 1, minWidth: 120 }}>
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            label="IFSC Code :"
                                            onBlur={handleBlur}
                                            name={`bankDetails.${index}.IFSCCode`}
                                            onChange={e => setFieldValue(`bankDetails[${index}].IFSCCode`, e.target.value)}
                                            value={values.bankDetails?.[index]?.IFSCCode || ''}
                                            error={!!touched.bankDetails?.[index]?.IFSCCode && !!errors.bankDetails?.[index]?.IFSCCode}
                                            helperText={touched.bankDetails?.[index]?.IFSCCode && errors.bankDetails?.[index]?.IFSCCode}
                                            sx={{ gridColumn: "span 2" }}
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text.primary
                                                }
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                            ))}
                            <Box display="flex" justifyContent="flex-end" m={1}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddBank}
                                >
                                    Add Bank Details
                                </Button>
                            </Box>
                        </>
                    )}
                    <FormControl>
                        <Stack direction="row" spacing={2} sx={{ m: 1, minWidth: 120 }}>
                            <Button
                                variant="contained"
                                disabled={activeStep === 0}
                                onClick={handleBack}
                            >
                                Back
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                type="button"
                                onClick={() => handleNext(values)}
                            >
                                {isLastStep ? (id ? 'Update' : 'Submit') : 'Next'}
                            </Button>
                        </Stack>
                    </FormControl>
                </Stack>
            </form>
        </Box >
    );
}

